import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import Resizer from 'react-image-file-resizer';

import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Publish from '@material-ui/icons/Publish';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';

import compose from 'helpers/compose';
import FreeSwitch from 'components/FormFields/FreeSwitch';
import FileUpload from 'components/FormFields/FileUpload';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Message, { MessageVariant } from 'components/Message/Message';

import { RootStore } from 'stores/rootStore';
import { LogoStore } from 'stores/advisor/logoStore';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    image: {
      border: 'solid #ccc 1px',
      maxWidth: '100%',
      position: 'relative',
      left: '50%',
      transform: 'translate(-50%, 0)',
      '&:hover': {
        border: 'solid #999 1px',
        background: '#eee',
      },
    },
  }),
);

interface Props {
  opened: boolean;
  handleClose: () => void;
  rootStore?: RootStore;
}

const MIN_WIDTH = 500;
const MAX_WIDTH = 500;
const MIN_HEIGHT = 140;
const MAX_HEIGHT = 140;
const ALLOWED_FORMATS = '.png';

const UserLogo: React.FC<Props> = ({ opened, handleClose, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [store] = useState(new LogoStore(rootStore!));

  const [base64, setBase64] = useState<string | undefined>();
  const imgRef = React.createRef<HTMLImageElement>();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [ratio, setRatio] = useState(0);
  const [resize, setResize] = useState(false);
  const [tooSmall, setTooSmall] = useState(false);
  const [file, setFile] = useState(undefined);
  const [fileName, setFilename] = useState(undefined);
  const [uploadOpened, setUploadOpened] = useState(false);

  const handleResize = useCallback(async () => {
    if (file) {
      const newBase64 = await resizeFile(file);
      setBase64(newBase64 as string);
    }
  },[file]);

  useEffect(() => {
    (async () => {
      if (imgRef.current) {
        setWidth(imgRef.current.naturalWidth);
        setHeight(imgRef.current.naturalHeight);
        setRatio(imgRef.current.naturalWidth / imgRef.current.naturalHeight);
        if (!resize && (width < MIN_HEIGHT || height < MIN_HEIGHT)) {
          setTooSmall(true);
        } else if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          setResize(true);
          setTooSmall(false);
          await handleResize()
        }
      }
    })();
  }, [imgRef, width, height, resize, handleResize]);

  useEffect(() => {
    store.fetchLogo();
  }, [store]);

  const handleReaderLoaded = (readerEvt: any) => {
    let binaryString = readerEvt.target.result;
    setBase64(`data:image/png;base64,${btoa(binaryString)}`);
  };

  const onImportChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setResize(false);
      setTooSmall(false);
      setFile(file);
      setFilename(file.name);
      const reader = new FileReader();
      reader.onload = handleReaderLoaded;
      reader.readAsBinaryString(file);
    }
  };

  const resizeFile = (file: any) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        MAX_WIDTH,
        MAX_HEIGHT,
        'PNG',
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'base64',
      );
    });

  return (
    <>
      <Dialog open={opened} maxWidth="sm">
        <DialogHeader onClose={handleClose}>{t('navMenu_logo')}</DialogHeader>
        <DialogContent>
          {store.data && (
            <>
              {store.data.data ? (
                <img
                  src={store.data.data}
                  alt=""
                  className={classes.image}
                />
              ) : (
                <p>
                  <em>{t('advisor_logo_noCustomLogo')}</em>
                </p>
              )}
              <FreeSwitch
                id={'edward_logo'}
                label={`${t('advisor_logo_none')}:`}
                checked={!store.data.custom}
                afterChange={async () => store.setCustom(!store.data!.custom)}
                fullWidth
                disabled={!store.data.data}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={async () => setUploadOpened(true)} variant="outlined" startIcon={<Publish />}>
            {t('advisor_logo_uploadFile')}
          </Button>
          <Button
            onClick={async () => store.deleteLogo()}
            variant="contained"
            color="default"
            disabled={!store.data?.data}
          >
            {t('advisor_logo_delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpened} maxWidth="sm">
        <DialogHeader onClose={() => setUploadOpened(false)}>{t('advisor_logo_uploadFile')}</DialogHeader>
        <DialogContent>
          <p
            dangerouslySetInnerHTML={{
              __html: t('advisor_logo_info', {
                interpolation: { escapeValue: false },
                minWidth: MIN_WIDTH,
                minHeight: MIN_HEIGHT,
                ratio: `${(MIN_WIDTH / MIN_HEIGHT).toFixed(0)} : 1`,
              }),
            }}
          />

          <img src={base64} alt="" style={{ width }} ref={imgRef} className={classes.image} />
          <br />
          {width !== 0 && height !== 0 && ratio !== 0 && (
            <>
              {t('advisor_logo_file')}: <strong>{fileName}</strong>
              <br />
              {t('advisor_logo_size')}:{' '}
              <strong>
                {width} × {height} px
              </strong>
              {file && resize && t('advisor_logo_resized')}
              <br />
              {t('advisor_logo_ratio')}: <strong>{ratio.toFixed(2)} : 1</strong>
            </>
          )}
          {file && tooSmall && (
            <Message
              id={`advisorLogo_wrongSize`}
              message={t('advisor_logo_tooSmall')}
              variant={MessageVariant.WARNING}
            />
          )}
        </DialogContent>
        <DialogActions>
          <FileUpload name="logo" accept={ALLOWED_FORMATS} onChange={(e: any) => onImportChange(e)} noFileName />
          <Button
            onClick={async () => {
              await store.uploadLogo(base64);
              setUploadOpened(false);
            }}
            variant="contained"
            color="primary"
            disabled={!file || tooSmall}
            startIcon={<Publish />}
          >
            {t('general_upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default compose(inject('rootStore'), observer)(UserLogo);
